import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTheme } from "../lib/useTheme";

// Bottom tab bar shown on phones (≤900px, see styles.css). Desktop keeps the
// appbar nav. Five slots: the three main destinations, the Generate action,
// and a More sheet holding the secondary actions.

function IconBook() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  );
}

function IconTable() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="12" cy="6" rx="9" ry="3" />
      <path d="M3 6v2c0 1.2 2 2.3 5 2.8L7 21M21 6v2c0 1.2-2 2.3-5 2.8L17 21" />
    </svg>
  );
}

function IconSpark() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2l1.9 5.7a2 2 0 0 0 1.3 1.3L21 11l-5.8 1.9a2 2 0 0 0-1.3 1.3L12 20l-1.9-5.8a2 2 0 0 0-1.3-1.3L3 11l5.8-2a2 2 0 0 0 1.3-1.3L12 2z" />
    </svg>
  );
}

function IconCloche() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 17h18" />
      <path d="M5 17a7 7 0 0 1 14 0" />
      <path d="M12 10v-1.5" />
      <circle cx="12" cy="7.5" r="1" />
      <path d="M2 20h20" />
    </svg>
  );
}

function IconMore() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <circle cx="5" cy="12" r="1.8" />
      <circle cx="12" cy="12" r="1.8" />
      <circle cx="19" cy="12" r="1.8" />
    </svg>
  );
}

export function MobileTabBar({
  passCount,
  signedIn,
  onGenerate,
  onTour,
  onIntro,
  onSignOut,
}: {
  passCount: number;
  signedIn: boolean;
  onGenerate: () => void;
  onTour: () => void;
  onIntro: () => void;
  onSignOut: () => void;
}) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const navigate = useNavigate();
  const { theme, toggle: toggleTheme } = useTheme();
  const closeSheet = () => setSheetOpen(false);

  return (
    <>
      {sheetOpen && <div className="tabbar-backdrop" onClick={closeSheet} />}
      {sheetOpen && (
        <div className="tabbar-sheet" role="menu">
          <button
            type="button"
            onClick={() => {
              closeSheet();
              navigate("/on-deck");
            }}
          >
            On Deck
          </button>
          <button
            type="button"
            onClick={() => {
              closeSheet();
              navigate("/shopping");
            }}
          >
            Shopping List
          </button>
          <button
            type="button"
            onClick={() => {
              closeSheet();
              navigate("/preferences");
            }}
          >
            Preferences
          </button>
          <button type="button" onClick={() => toggleTheme()}>
            {theme === "dark" ? "Light Mode" : "Dark Mode"}
          </button>
          <button
            type="button"
            onClick={() => {
              closeSheet();
              navigate("/connect");
            }}
          >
            <span className="spark">⚡</span> Assistant
          </button>
          {signedIn && (
            <button
              type="button"
              onClick={() => {
                closeSheet();
                onTour();
              }}
            >
              Replay Tour
            </button>
          )}
          {signedIn && (
            <button
              type="button"
              onClick={() => {
                closeSheet();
                onIntro();
              }}
            >
              Replay Intro
            </button>
          )}
          {signedIn && (
            <button
              type="button"
              onClick={() => {
                closeSheet();
                onSignOut();
              }}
            >
              Sign Out
            </button>
          )}
        </div>
      )}
      <nav className="tabbar">
        <NavLink to="/" end className="tabbar-item" onClick={closeSheet} data-tour-id="nav-cookbook">
          <IconBook />
          <span>Cookbook</span>
        </NavLink>
        <NavLink to="/community" className="tabbar-item" onClick={closeSheet} data-tour-id="nav-community">
          <IconTable />
          <span>Kitchen</span>
        </NavLink>
        <button
          type="button"
          className="tabbar-item tabbar-gen"
          data-tour-id="chat-trigger-mobile"
          onClick={() => {
            closeSheet();
            onGenerate();
          }}
        >
          <span className="tabbar-gen-circle">
            <IconSpark />
          </span>
          <span>Generate</span>
        </button>
        <NavLink to="/served" className="tabbar-item" onClick={closeSheet} data-tour-id="nav-served">
          <span className="tabbar-icon-wrap">
            <IconCloche />
            {passCount > 0 && <span className="tabbar-badge">{passCount}</span>}
          </span>
          <span>Served</span>
        </NavLink>
        <button
          type="button"
          className={"tabbar-item" + (sheetOpen ? " active" : "")}
          onClick={() => setSheetOpen((v) => !v)}
        >
          <IconMore />
          <span>More</span>
        </button>
      </nav>
    </>
  );
}
